import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  IRegexValidation,
  IRegexValidationCredential,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";

type RegexValidationCredentialsDialogProps = {
  regexValidation: IRegexValidation;
  onClose: () => void;
};

const RegexValidationCredentialsDialog = ({
  regexValidation,
  onClose,
}: RegexValidationCredentialsDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { licenseService, licenseDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("displayName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const listRequest = ListRequestOfBaseFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
  });

  // Loaded only once the dialog is open, so the list itself never carries the assignments.
  const { isError, data, error, isFetching } = useQuery(
    [
      "licenseService.getRegexValidationCredentials",
      regexValidation.id,
      listRequest,
    ],
    () =>
      licenseService.getRegexValidationCredentials(
        regexValidation.id!,
        listRequest
      ),
    { keepPreviousData: true }
  );

  const CredentialCell = ({ value }: { value: IRegexValidationCredential }) => (
    <Link
      href={licenseDetailsUrl(value.id)}
      target="_blank"
      rel="noopener"
      color="inherit"
      underline="hover"
    >
      {value.displayName}
      <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5, mb: -0.25 }} />
    </Link>
  );

  // Down a column of hundreds of rows the same sentence repeated is noise. Small chips carry the
  // same facts - is it the shared default, who reached it anyway, and who never gets it - and can
  // be told apart at a glance.
  const AppliedCell = ({ value }: { value: IRegexValidationCredential }) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {value.appliedByDefault && (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={t({ id: "regexValidationAppliedByDefault" })}
        />
      )}
      {!!value.organizationsUsingThisValidationCount && (
        <Chip
          size="small"
          variant="outlined"
          label={t(
            { id: "regexValidationAppliedByOrganizations" },
            { count: value.organizationsUsingThisValidationCount }
          )}
        />
      )}
      {!!value.organizationsSkippingThisValidationCount && (
        <Tooltip
          title={t({ id: "regexValidationSkippedByOrganizationsTooltip" })}
        >
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t(
              { id: "regexValidationSkippedByOrganizations" },
              { count: value.organizationsSkippingThisValidationCount }
            )}
          />
        </Tooltip>
      )}
    </Stack>
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "displayName",
      type: "node",
      View: CredentialCell,
      label: t({ id: "regexValidationCredentialLabel" }),
    },
    {
      accessor: "appliedByDefault",
      type: "node",
      View: AppliedCell,
      // Sortable on purpose: almost every credential takes the validation as its standard, so the few
      // that do not are the ones worth finding, and this brings them to the top.
      label: t({ id: "regexValidationAppliedThroughLabel" }),
    },
  ];

  const rows = ((data?.data as IRegexValidationCredential[]) ?? []).map((c) => ({
    ...c,
    id: c.id!,
  }));

  return (
    <>
      <DialogTitle>
        {t({ id: "regexValidationCredentialsTitle" })}
        <Typography variant="body2" color="text.secondary">
          {regexValidation.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pb: 2 }}>
        {isError ? (
          <Alert severity="error">{translateError(error, t)}</Alert>
        ) : (
          <LazyTable
            {...{
              rowCount: data?.totalRecords ?? 0,
              rows,
              loading: isFetching,
              dense: true,
              cells,
              page,
              setPage,
              order,
              setOrder,
              orderBy,
              setOrderBy,
              rowsPerPage,
              setRowsPerPage,
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t({ id: "closeButton" })}</Button>
      </DialogActions>
    </>
  );
};

export default RegexValidationCredentialsDialog;
